import { ChangeDetectionStrategy, Component, computed, effect, signal, ViewEncapsulation } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { WorkflowActionInputComponent } from "./workflow.action.input.component";
import { SystemActionComponent } from "./abstract.system.action.component";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { WorkflowContactRoleFieldRefsComponent } from "./workflow.contact.role.field.refs.component";
import { Workflow } from "../service/Workflow";
import { Item } from "../../../pojo/Item";
import { WorkflowCustomAttributeMappingsComponent } from "./workflow.custom.attribute.mappings.component";
import {
    parseWorkflowCustAtrbMappings,
    serializeWorkflowCustAtrbMappings,
    WorkflowCustAtrbMappingUiRow,
} from "./workflow.cust.atrb.mappings.util";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-create-org-action",
    template: `
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['acctNameField']?.inputValue"
            (valueChange)="setInputValue('acctNameField', $event)"
            [label]="'Organization Name Field'"
            [type]="'text'"
            [required]="true"
            [help]="
                'Map a form field containing the organization name (e.g., $CNET-123-ORG_NAME). Required. Maximum 40 characters.'
            "
            [acceptedAnswerTypes]="acceptedFor('acctNameField')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['acctTitleField']?.inputValue"
            (valueChange)="setInputValue('acctTitleField', $event)"
            [label]="'Organization Title Field'"
            [type]="'text'"
            [required]="false"
            [help]="
                'Optional: Map a form field containing the organization title (e.g., $CNET-456-ORG_TITLE). Maximum 80 characters.'
            "
            [acceptedAnswerTypes]="acceptedFor('acctTitleField')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['acctNumberField']?.inputValue"
            (valueChange)="setInputValue('acctNumberField', $event)"
            [label]="'Organization Number Field'"
            [type]="'text'"
            [required]="false"
            [help]="
                'Optional: Map a form field containing the organization number (e.g., $CNET-789-ORG_NUMBER). Maximum 40 characters.'
            "
            [acceptedAnswerTypes]="acceptedFor('acctNumberField')"
        />

        <div class="form-group">
            <label>Organization Type</label>
            <s25-ng-dropdown-search-criteria
                [type]="'organizationTypes'"
                [value]="selectedOrgType()"
                (valueChange)="onOrgTypeChange($event)"
                [placeholder]="'Select Organization Type'"
            />
            <small class="form-text">Fallback organization type if not provided by form data</small>
        </div>

        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['orgTypeIdField']?.inputValue"
            (valueChange)="setInputValue('orgTypeIdField', $event)"
            [label]="'Organization Type ID Field'"
            [type]="'text'"
            [required]="false"
            [help]="
                'Optional: Map a form field containing the organization type ID (e.g., $CNET-101-ORG_TYPE_ID). If not provided, the organization type selected above will be used.'
            "
            [acceptedAnswerTypes]="acceptedFor('orgTypeIdField')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['primeContactField']?.inputValue"
            (valueChange)="setInputValue('primeContactField', $event)"
            [label]="'Primary Contact ID Field'"
            [type]="'text'"
            [required]="false"
            [help]="'Optional: Map a form field containing the primary contact ID (e.g., $CNET-202-CONTACT_ID).'"
            [acceptedAnswerTypes]="acceptedFor('primeContactField')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['commentField']?.inputValue"
            (valueChange)="setInputValue('commentField', $event)"
            [label]="'Comment Field'"
            [type]="'text'"
            [required]="false"
            [help]="'Optional: Map a form field containing a comment for the organization (e.g., $CNET-303-COMMENT).'"
            [acceptedAnswerTypes]="acceptedFor('commentField')"
        />

        <div class="form-group">
            <small class="form-text auto-collect-info">
                Organization categories and contact/role pairs from ORG_CATEGORIES and ORG_CONTACT_ROLE form fields are
                automatically collected. Optionally add static categories below.
            </small>
        </div>

        <div class="form-group">
            <label class="checkbox-option">
                <input
                    type="checkbox"
                    [checked]="skipAddingContactOrgRoles()"
                    (change)="onSkipAddingContactOrgRolesToggle($any($event.target).checked)"
                />
                Skip adding contact roles from ORG_CONTACT_ROLE form fields
            </label>
            <small class="form-text">
                When checked, roles from ORG_CONTACT_ROLE answers are not added to the new organization. Contact role
                field references below are still applied.
            </small>
        </div>

        <s25-ng-workflow-contact-role-field-refs
            [(inputs)]="inputs"
            (changed)="changed.emit()"
            [inputIdGenerator]="inputIdGenerator()"
        />

        @if (loading()) {
            <div class="form-group"><em>Loading...</em></div>
        } @else {
            <div class="form-group">
                <label class="checkbox-option">
                    <input type="checkbox" [checked]="showStaticCategories()" (change)="onToggleStaticCategories()" />
                    Add specific organization categories
                </label>
                @if (showStaticCategories()) {
                    <div class="sub-section">
                        <s25-ng-multiselect-search-criteria
                            [type]="'organizationCategories'"
                            [selectedItems]="selectedCategories()"
                            (changed)="onCategoriesChange($event)"
                            [showResults]="true"
                        />
                    </div>
                }
            </div>

            <div class="form-group">
                <label class="checkbox-option">
                    <input
                        type="checkbox"
                        [checked]="useManualCustAtrbMappings()"
                        (change)="onToggleManualCustAtrbMappings($any($event.target).checked)"
                    />
                    Map organization custom attributes manually
                </label>
            </div>

            @if (useManualCustAtrbMappings()) {
                <s25-ng-workflow-custom-attribute-mappings
                    [itemTypeId]="itemIds.Organization"
                    [mappings]="custAtrbMappings()"
                    (mappingsChange)="onCustAtrbMappingsChange($event)"
                />
            }
        }

        <h5 class="section-heading">Address Fields (Optional)</h5>

        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['adminAddress']?.inputValue"
            (valueChange)="setInputValue('adminAddress', $event)"
            [label]="'Administrative Address'"
            [type]="'text'"
            [required]="false"
            [help]="'Street address for administrative address (max 120 chars).'"
            [acceptedAnswerTypes]="acceptedFor('adminAddress')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['adminCity']?.inputValue"
            (valueChange)="setInputValue('adminCity', $event)"
            [label]="'Administrative City'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('adminCity')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['adminState']?.inputValue"
            (valueChange)="setInputValue('adminState', $event)"
            [label]="'Administrative State/Province'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('adminState')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['adminZip']?.inputValue"
            (valueChange)="setInputValue('adminZip', $event)"
            [label]="'Administrative Zip/Postal Code'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('adminZip')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['adminCountry']?.inputValue"
            (valueChange)="setInputValue('adminCountry', $event)"
            [label]="'Administrative Country'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('adminCountry')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['adminPhone']?.inputValue"
            (valueChange)="setInputValue('adminPhone', $event)"
            [label]="'Administrative Phone'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('adminPhone')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['adminFax']?.inputValue"
            (valueChange)="setInputValue('adminFax', $event)"
            [label]="'Administrative Fax'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('adminFax')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['billingAddress']?.inputValue"
            (valueChange)="setInputValue('billingAddress', $event)"
            [label]="'Billing Address'"
            [type]="'text'"
            [required]="false"
            [help]="'Street address for billing address (max 120 chars).'"
            [acceptedAnswerTypes]="acceptedFor('billingAddress')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['billingCity']?.inputValue"
            (valueChange)="setInputValue('billingCity', $event)"
            [label]="'Billing City'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('billingCity')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['billingState']?.inputValue"
            (valueChange)="setInputValue('billingState', $event)"
            [label]="'Billing State/Province'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('billingState')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['billingZip']?.inputValue"
            (valueChange)="setInputValue('billingZip', $event)"
            [label]="'Billing Zip/Postal Code'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('billingZip')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['billingCountry']?.inputValue"
            (valueChange)="setInputValue('billingCountry', $event)"
            [label]="'Billing Country'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('billingCountry')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['billingPhone']?.inputValue"
            (valueChange)="setInputValue('billingPhone', $event)"
            [label]="'Billing Phone'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('billingPhone')"
        />
        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['billingFax']?.inputValue"
            (valueChange)="setInputValue('billingFax', $event)"
            [label]="'Billing Fax'"
            [type]="'text'"
            [required]="false"
            [acceptedAnswerTypes]="acceptedFor('billingFax')"
        />
    `,
    styles: `
        .form-group {
            margin-bottom: 16px;
        }

        .form-group label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: #555;
        }

        .form-text {
            color: #666;
            font-size: 12px;
            margin-top: 4px;
            display: block;
        }

        .auto-collect-info {
            color: #555;
            font-size: 13px;
            font-style: italic;
        }

        .system-input {
            margin-bottom: 16px;
        }

        .system-input:last-child {
            margin-bottom: 0;
        }

        .section-heading {
            margin: 24px 0 12px;
            padding-top: 12px;
            border-top: 1px solid #dee2e6;
            color: #333;
        }

        .checkbox-option {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            font-weight: normal;
        }

        .checkbox-option input[type="checkbox"] {
            margin: 0;
        }

        .sub-section {
            margin-top: 8px;
            margin-left: 24px;
        }

        .mapping-row {
            display: flex;
            gap: 8px;
            align-items: center;
            margin-bottom: 8px;
        }

        .form-control--inline {
            flex: 1;
            min-width: 0;
        }

        .btn-remove {
            flex-shrink: 0;
            width: 32px;
            height: 34px;
            padding: 0;
            border: 1px solid #ccc;
            border-radius: 4px;
            background: #f8f9fa;
            font-size: 18px;
            line-height: 1;
            cursor: pointer;
            color: #666;
        }

        .btn-remove:hover {
            background: #e9ecef;
            color: #333;
        }

        .btn-add {
            margin-top: 4px;
            padding: 6px 12px;
            border: 1px solid #2573a7;
            border-radius: 4px;
            background: #fff;
            color: #2573a7;
            font-size: 14px;
            cursor: pointer;
        }

        .btn-add:hover {
            background: #f0f7fc;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        FormsModule,
        WorkflowActionInputComponent,
        S25DropdownPaginatedModule,
        S25MultiselectModule,
        WorkflowContactRoleFieldRefsComponent,
        WorkflowCustomAttributeMappingsComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateOrgActionComponent extends SystemActionComponent {
    private static readonly ACTION_CONFIG_INPUT_CODE = "actionConfig";

    protected readonly actionCode = "CREATE_ORG" as const;
    readonly loading = signal(true);
    readonly showStaticCategories = signal(false);
    readonly selectedCategories = signal<S25ItemI[]>([]);
    readonly itemIds = Item.Ids;
    readonly useManualCustAtrbMappings = signal(false);
    readonly custAtrbMappings = signal<WorkflowCustAtrbMappingUiRow[]>([]);
    /** Mirrors WorkflowActionConfig.skipAddingContactOrgRoles for the checkbox UI. */
    readonly skipAddingContactOrgRoles = signal(false);
    private initialized = false;

    readonly selectedOrgType = computed<DropDownItem | null>(() => {
        const orgTypeIdInput = this.inputMap()["orgTypeId"];
        if (!orgTypeIdInput?.inputValue) return null;
        const orgTypeId = parseInt(orgTypeIdInput.inputValue, 10);
        if (isNaN(orgTypeId)) return null;
        return {
            itemId: orgTypeId,
            itemTypeId: undefined,
            itemName: "",
            txt: "",
            val: orgTypeId.toString(),
        } as DropDownItem;
    });

    constructor() {
        super();

        effect(() => {
            const inputs = this.inputMap();
            if (this.initialized) return;
            this.initialized = true;
            this.initializeFromInputs(inputs).finally(() => this.loading.set(false));
        });
    }

    private async initializeFromInputs(inputs: Record<string, { inputValue?: string } | undefined>) {
        const categoryIds = inputs["orgCategoryIds"]?.inputValue;
        if (categoryIds) {
            this.showStaticCategories.set(true);
            this.selectedCategories.set(await this.parseCategoryIdsAndFetchNames(categoryIds));
        }

        const parsed = parseWorkflowCustAtrbMappings(inputs["orgCustomAttributes"]?.inputValue);
        if (parsed.length) {
            this.useManualCustAtrbMappings.set(true);
            this.custAtrbMappings.set(parsed);
        }

        const actionConfigParsed = this.parseActionConfigObject(
            inputs[CreateOrgActionComponent.ACTION_CONFIG_INPUT_CODE]?.inputValue,
        );
        this.skipAddingContactOrgRoles.set(actionConfigParsed.skipAddingContactOrgRoles === true);
    }

    private async parseCategoryIdsAndFetchNames(json: string): Promise<S25ItemI[]> {
        try {
            const categoryIds: number[] = JSON.parse(json);
            if (!categoryIds.length) return [];
            const categories = await SearchCriteriaService.getOrganizationCategories();
            const categoryMap = new Map<number, string>(
                categories.map((cat: any) => [cat.itemId, String(cat.itemName || "")]),
            );
            return categoryIds.map((id) => ({
                itemId: id,
                itemName: (categoryMap.get(id) as string | undefined) || `Category ${id}`,
            }));
        } catch {
            return [];
        }
    }

    onOrgTypeChange(orgType: DropDownItem | null) {
        if (orgType?.itemId) {
            this.setInputValue("orgTypeId", orgType.itemId.toString());
        } else {
            this.setInputValue("orgTypeId", "");
        }
    }

    onSkipAddingContactOrgRolesToggle(checked: boolean) {
        this.skipAddingContactOrgRoles.set(checked);
        const previous = this.parseActionConfigObject(
            this.inputMap()[CreateOrgActionComponent.ACTION_CONFIG_INPUT_CODE]?.inputValue,
        );
        const next: Workflow.WorkflowActionConfig = { ...previous };
        if (checked) {
            next.skipAddingContactOrgRoles = true;
        } else {
            delete next.skipAddingContactOrgRoles;
        }
        this.setInputValue(CreateOrgActionComponent.ACTION_CONFIG_INPUT_CODE, this.actionConfigToInputValue(next));
    }

    private parseActionConfigObject(raw: string | undefined): Workflow.WorkflowActionConfig {
        if (!raw?.trim()) {
            return {};
        }
        try {
            const parsed = JSON.parse(raw) as Workflow.WorkflowActionConfig;
            if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
                return {};
            }
            return parsed;
        } catch {
            return {};
        }
    }

    private actionConfigToInputValue(config: Workflow.WorkflowActionConfig): string {
        const cleaned: Workflow.WorkflowActionConfig = { ...config };
        if (!cleaned.skipAddingContactOrgRoles) {
            delete cleaned.skipAddingContactOrgRoles;
        }
        if (Object.keys(cleaned).length === 0) {
            return "";
        }
        return JSON.stringify(cleaned);
    }

    onToggleStaticCategories() {
        const show = !this.showStaticCategories();
        this.showStaticCategories.set(show);
        if (!show) {
            this.setInputValue("orgCategoryIds", "");
            this.selectedCategories.set([]);
        }
    }

    onCategoriesChange(items: S25ItemI[]) {
        this.selectedCategories.set(items);
        this.setInputValue("orgCategoryIds", JSON.stringify(items.map((item) => item.itemId)));
    }

    onToggleManualCustAtrbMappings(checked: boolean) {
        this.useManualCustAtrbMappings.set(checked);
        if (!checked) {
            this.custAtrbMappings.set([]);
            this.setInputValue("orgCustomAttributes", "");
        }
    }

    onCustAtrbMappingsChange(rows: WorkflowCustAtrbMappingUiRow[]): void {
        this.custAtrbMappings.set(rows);
        this.setInputValue("orgCustomAttributes", serializeWorkflowCustAtrbMappings(rows));
    }
}
